/* FATE newsletter. Native dialog and Klaviyo's public Client Subscription API. */
(function () {
  const REVISION = '2026-07-15';
  const SEEN_KEY = 'fate-newsletter-mystery-discount-seen-v1';
  const PAUSE_MS = 7200000;
  const validEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  function subscriptionRequest(email, { publicKey, listId, source }) {
    if (!publicKey || !listId) throw new Error('missing-configuration');
    const address = email.trim();
    if (!validEmail(address)) throw new Error('invalid-email');
    return {
      url: `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION },
        body: JSON.stringify({ data: {
          type: 'subscription',
          attributes: { custom_source: source, profile: { data: { type: 'profile', attributes: {
            email: address, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } } },
          relationships: { list: { data: { type: 'list', id: listId } } }
        } })
      }
    };
  }
  async function subscribe(email, config, fetcher, signal) {
    const request = subscriptionRequest(email, config);
    const response = await fetcher(request.url, { ...request.options, signal });
    if (response.status !== 202) throw new Error(response.status === 429 ? 'rate-limit' : 'subscription-failed');
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { subscriptionRequest, subscribe, validEmail };
  if (typeof customElements === 'undefined' || customElements.get('fate-newsletter-popup')) return;

  customElements.define('fate-newsletter-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const dialog = this.querySelector('dialog');
      if (!dialog) return;
      const form = this.querySelector('[data-newsletter-form]');
      const input = form.querySelector('[name="email"]');
      const button = form.querySelector('[data-newsletter-submit]');
      const error = form.querySelector('[data-newsletter-error]');
      const events = new AbortController();
      const on = (target, event, callback) => target.addEventListener(event, callback, { signal: events.signal });
      const preview = Boolean(window.Shopify?.designMode) ||
        (this.dataset.dev === 'true' && new URLSearchParams(location.search).get('fate-newsletter') === '1');
      let opener, requestTimer, requestController, priorFocus, shown = false, pending = false;
      const submitLabel = button.textContent;
      const config = { publicKey: this.dataset.publicKey, listId: this.dataset.listId, source: this.dataset.source };
      const seen = () => {
        if (shown) return true;
        try { const last = Number(localStorage.getItem(SEEN_KEY)); return last > 0 && Date.now() - last < PAUSE_MS; }
        catch { return false; }
      };
      const setStep = name => {
        dialog.dataset.step = name;
        this.querySelectorAll('[data-newsletter-step]').forEach(step => {
          step.hidden = step.dataset.newsletterStep !== name;
          if (!step.hidden) {
            dialog.setAttribute('aria-labelledby', step.querySelector('h2').id);
            (name === 'email' ? input : step.querySelector('h2')).focus({ preventScroll: true });
          }
        });
      };
      const busy = value => {
        pending = value;
        button.disabled = value;
        button.textContent = value ? 'ENVIANDO…' : submitLabel;
        input.readOnly = value;
        form.setAttribute('aria-busy', String(value));
      };
      const unlock = () => document.documentElement.classList.remove('fate-newsletter-open');
      const close = () => {
        clearTimeout(opener);
        clearTimeout(requestTimer);
        requestController?.abort();
        requestController = null;
        busy(false);
        if (dialog.open) dialog.close();
        unlock();
        if (priorFocus?.isConnected) priorFocus.focus({ preventScroll: true });
      };
      const show = () => {
        if (!this.isConnected || /\/cart\/?$/.test(location.pathname) || (!preview && seen())) return;
        if (document.hidden || document.querySelector('dialog[open], .fate-qs-sheet--open')) {
          opener = setTimeout(show, 5000);
          return;
        }
        priorFocus = document.activeElement;
        dialog.showModal();
        document.documentElement.classList.add('fate-newsletter-open');
        shown = true;
        if (!preview) { try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch {} }
      };
      on(input, 'input', () => { input.removeAttribute('aria-invalid'); error.hidden = true; });
      on(form, 'submit', async event => {
        event.preventDefault();
        if (pending) return;
        if (!input.validity.valid || !validEmail(input.value.trim())) {
          input.setAttribute('aria-invalid', 'true');
          error.textContent = 'Escribe un correo electrónico válido.';
          error.hidden = false;
          input.focus();
          return;
        }
        error.hidden = true;
        busy(true);
        const controller = new AbortController();
        requestController = controller;
        requestTimer = setTimeout(() => controller.abort(), 10000);
        try {
          await subscribe(input.value, config, window.fetch.bind(window), controller.signal);
          if (controller.signal.aborted || !this.isConnected || !dialog.open) return;
          input.value = '';
          setStep('success');
        } catch (failure) {
          if (!this.isConnected || !dialog.open) return;
          error.textContent = failure.message === 'rate-limit'
            ? 'Espera un momento e inténtalo de nuevo.'
            : 'No pudimos registrar tu correo. Inténtalo de nuevo.';
          error.hidden = false;
        } finally {
          clearTimeout(requestTimer);
          if (requestController === controller) { requestController = null; busy(false); }
        }
      });
      on(this.querySelector('[data-newsletter-close]'), 'click', close);
      on(this.querySelector('[data-newsletter-claim]'), 'click', close);
      on(dialog, 'cancel', event => { event.preventDefault(); close(); });
      on(dialog, 'close', unlock);
      on(dialog, 'click', event => {
        const box = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) close();
      });
      this.cleanup = () => { close(); events.abort(); this.cleanup = null; };
      if (preview || !seen()) opener = setTimeout(show, Number(this.dataset.delay) || 0);
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
})();
